import * as THREE from 'three';

/**
 * One shading model for everything the world module owns.
 *
 * MeshStandardMaterial costs roughly twice as much per fragment as this on the
 * software rasteriser, and three's Lambert bottoms out at pure black wherever
 * the hemisphere ground colour is dark — which is most of a backlit fortress.
 * So: wrapped lambert + hemisphere bounce with a floor + one Blinn lobe, fed by
 * three's own light/shadow/fog uniforms so it still tracks whatever the
 * lighting system decides to do.
 */
const COMMON_VERT = `
  #include <common>
  #include <fog_pars_vertex>
  #include <shadowmap_pars_vertex>
  uniform vec4 uUv;
  varying vec3 vNrm;
  varying vec3 vVPos;
  varying vec3 vCol;
  varying vec2 vUvS;
  varying vec2 vWXZ;
  void main() {
    vec3 objectNormal = normal;
    vec3 transformed = position;
    #ifdef USE_INSTANCING
      vec4 worldPosition = modelMatrix * instanceMatrix * vec4(transformed, 1.0);
      vec3 transformedNormal = normalMatrix * (mat3(instanceMatrix) * objectNormal);
    #else
      vec4 worldPosition = modelMatrix * vec4(transformed, 1.0);
      vec3 transformedNormal = normalMatrix * objectNormal;
    #endif
    vNrm = transformedNormal;
    vec3 c = vec3(1.0);
    #ifdef USE_COLOR
      c *= color;
    #endif
    #ifdef USE_INSTANCING_COLOR
      c *= instanceColor;
    #endif
    vCol = c;
    vUvS = uv * uUv.xy + uUv.zw;
    vec4 mvPosition = viewMatrix * worldPosition;
    vVPos = mvPosition.xyz;
    vWXZ = worldPosition.xz;
    gl_Position = projectionMatrix * mvPosition;
    #include <shadowmap_vertex>
    #include <fog_vertex>
  }`;

const COMMON_FRAG = `
  #include <common>
  #include <packing>
  #include <dithering_pars_fragment>
  #include <fog_pars_fragment>
  #include <lights_pars_begin>
  #include <shadowmap_pars_fragment>
  #include <shadowmask_pars_fragment>
  uniform vec4 uSurf;    // x wrap, y hemi gain, z spec power, w spec gain
  uniform vec3 uFloor;   // never-black floor, in OUTPUT units (post-BRDF)
  #ifdef SB_MAP
    uniform sampler2D uMap;
  #endif
  #ifdef SB_NORM
    uniform sampler2D uNormMap;
    uniform vec2 uNormScale;   // x,y strength on the tangent normal

    /**
     * Tangent basis recovered from screen-space derivatives.
     *
     * The world module's geometry is merged boxes and instanced solids built
     * by GeoBuilder, and none of it carries a tangent attribute — so the
     * frame is reconstructed per fragment from how position and uv change
     * across the triangle. This is why the module had no normal-map path at
     * all: without tangents there was nothing to map into.
     *
     * Here p is view-space position and N a view-space normal, which is the
     * space three's own light uniforms are in, so the perturbed normal drops
     * straight into the lighting below with no further transform.
     */
    mat3 cotangentFrame(vec3 N, vec3 p, vec2 uv) {
      vec3 dp1 = dFdx(p), dp2 = dFdy(p);
      vec2 duv1 = dFdx(uv), duv2 = dFdy(uv);
      vec3 dp2perp = cross(dp2, N), dp1perp = cross(N, dp1);
      vec3 T = dp2perp * duv1.x + dp1perp * duv2.x;
      vec3 B = dp2perp * duv1.y + dp1perp * duv2.y;
      float invmax = inversesqrt(max(dot(T, T), dot(B, B)));
      return mat3(T * invmax, B * invmax, N);
    }
  #endif
  uniform vec4 uShadowBox;   // xz centre, inner fade, outer fade
  varying vec3 vNrm;
  varying vec3 vVPos;
  varying vec3 vCol;
  varying vec2 vUvS;
  varying vec2 vWXZ;
  void main() {
    vec3 albedo = vCol;
    #ifdef SB_MAP
      albedo *= texture2D(uMap, vUvS).rgb;
    #endif
    #ifdef SB_FLAT
      vec3 n = normalize(cross(dFdx(vVPos), dFdy(vVPos)));
    #else
      vec3 n = normalize(vNrm);
      #ifdef SB_TWOSIDE
        if (!gl_FrontFacing) n = -n;
      #endif
    #endif
    #ifdef SB_NORM
      // A curtain wall is a box: every pixel of a face shares one interpolated
      // normal, so painted courses cannot self-shadow however the albedo is
      // weathered, and the wall stays a pale flat field under any light. This
      // is the term that gives the surface its own relief.
      vec3 nt = texture2D(uNormMap, vUvS).xyz * 2.0 - 1.0;
      nt.xy *= uNormScale;
      n = normalize(cotangentFrame(n, vVPos, vUvS) * nt);
    #endif
    float shadow = mix(getShadowMask(), 1.0, smoothstep(uShadowBox.z, uShadowBox.w, distance(vWXZ, uShadowBox.xy)));
    vec3 V = normalize(-vVPos);
    vec3 lit = ambientLightColor;
    vec3 spec = vec3(0.0);
    #if NUM_HEMI_LIGHTS > 0
      float hd = 0.5 * dot(n, hemisphereLights[0].direction) + 0.5;
      lit += mix(hemisphereLights[0].groundColor, hemisphereLights[0].skyColor, hd) * uSurf.y;
    #endif
    #if NUM_DIR_LIGHTS > 0
      vec3 L = directionalLights[0].direction;
      float ndl = dot(n, L);
      float wrap = clamp((ndl + uSurf.x) / (1.0 + uSurf.x), 0.0, 1.0);
      lit += directionalLights[0].color * (wrap * shadow);
      if (uSurf.w > 0.001) {
        vec3 H = normalize(L + V);
        spec = directionalLights[0].color * (pow(max(dot(n, H), 0.0), uSurf.z) * uSurf.w * shadow * step(0.0, ndl));
      }
    #endif
    // Every directional light after the key, shadowless. Must be a loop: the
    // cascade rig occupies indices 1..N-1 with colourless dummies and puts the
    // real sky fill after them, so directionalLights[1] is a black light.
    #if NUM_DIR_LIGHTS > 1
      for (int i = 1; i < NUM_DIR_LIGHTS; i++) {
        lit += directionalLights[i].color * clamp((dot(n, directionalLights[i].direction) + uSurf.x) / (1.0 + uSurf.x), 0.0, 1.0);
      }
    #endif
    // lit is an irradiance sum, so RECIPROCAL_PI (the Lambert BRDF) belongs on
    // it - that is exactly what three does to ambient, hemisphere and
    // directional light for every OTHER material in the scene, and world
    // surfaces have to sit at the same exposure as the characters standing on
    // them. uFloor is NOT one of those terms: it is this module's own authored
    // never-black floor, in output units. Dividing it by pi as well put it at
    // (0.027, 0.039, 0.067) while the hard-coded floors in terrain.js,
    // props.js and bases.js - which are written as post-BRDF constants - land
    // at (0.083, 0.102, 0.134). That factor of three is the whole reason a
    // boulder read as a black faceted pyramid while the ground it sat on did
    // not. It is added after the BRDF now, so floor x floorGain means what it
    // says.
    gl_FragColor = vec4(albedo * (uFloor + lit * RECIPROCAL_PI) + spec, 1.0);
    #include <tonemapping_fragment>
    #include <colorspace_fragment>
    #include <fog_fragment>
    #include <dithering_fragment>
  }`;

/**
 * @param {object} o
 *  map        THREE.Texture | null
 *  uvScale    [sx, sy] applied to the geometry uv
 *  wrap       terminator softness (0.0 hard .. 0.6 very soft)
 *  hemi       hemisphere bounce gain
 *  spec       [power, gain]
 *  floor      never-black output floor (hex, sRGB); added AFTER the Lambert
 *             BRDF, so `floor` x `floorGain` is the linear value a fully
 *             unlit facet of a white surface resolves to
 *  side       THREE side constant
 *  flat       flat shading from screen-space derivatives
 *  normalMap  THREE.Texture | null, tangent-space, LINEAR (never sRGB)
 *  normalScale [sx, sy] strength on the tangent normal's xy
 */
/** Every surface material's uniforms, so the shadow box can be pushed once a frame. */
export const SURFACE_UNIS = [];

/** Follow the directional-light shadow frustum so its edge never shows. */
export function setShadowBox(x, z, r) {
  for (let i = 0; i < SURFACE_UNIS.length; i++) SURFACE_UNIS[i].uShadowBox.value.set(x, z, r * 0.68, r * 0.94);
}

export function surfaceMaterial(o = {}) {
  const defines = {};
  if (o.map) defines.SB_MAP = '';
  if (o.normalMap) defines.SB_NORM = '';
  if (o.flat) defines.SB_FLAT = '';
  if (o.side === THREE.DoubleSide) defines.SB_TWOSIDE = '';
  const uni = THREE.UniformsUtils.merge([THREE.UniformsLib.lights, THREE.UniformsLib.fog, {
    uMap: { value: null },
    uNormMap: { value: null },
    uNormScale: { value: new THREE.Vector2(1, 1) },
    uSurf: { value: new THREE.Vector4() },
    uFloor: { value: new THREE.Color() },
    uUv: { value: new THREE.Vector4() },
    uShadowBox: { value: new THREE.Vector4(0, 0, 1e6, 1e7) },
  }]);
  uni.uMap.value = o.map || null;
  uni.uNormMap.value = o.normalMap || null;
  const ns = o.normalScale || [1, 1];
  uni.uNormScale.value.set(ns[0], ns[1]);
  const spec = o.spec || [24, 0];
  uni.uSurf.value.set(o.wrap ?? 0.28, o.hemi ?? 1.0, spec[0], spec[1]);
  uni.uFloor.value.setHex(o.floor ?? 0x14181e).multiplyScalar(o.floorGain ?? 3.0);
  const uv = o.uvScale || [1, 1];
  uni.uUv.value.set(uv[0], uv[1], 0, 0);

  const m = new THREE.ShaderMaterial({
    uniforms: uni, defines, lights: true, fog: true, dithering: true,
    side: o.side ?? THREE.FrontSide,
    vertexColors: o.vertexColors !== false,
    vertexShader: COMMON_VERT,
    fragmentShader: COMMON_FRAG,
  });
  m.userData.uni = uni;
  SURFACE_UNIS.push(uni);
  return m;
}
